import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, Stack, TextField,
  Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { flowService } from '../../services/flowService';
import { deliveryService } from '../../services/deliveryService';
import bookingService from '../../services/bookingService';
import { iconFor as connectorIconFor } from '../deliveries/connectorIcons';
import FlowTriggerDialog from '../FlowBuilder/FlowTriggerDialog';
import DestinationDialog from '../deliveries/DestinationDialog';

const DEVELOPERS_URL = 'https://www.flowgate.co.uk/developers';

/** "3 min ago", "2 h ago", "4 days ago": how long since, in the fewest words that say it. */
export const since = (iso, now = Date.now()) => {
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

/**
 * What the last call to this flow's live data did, as one line with a colour. An error screen is
 * still a working endpoint: it is the customer being told "no times free that day", on purpose.
 */
export const liveStatus = (live) => {
  if (!live?.lastCalledAt) return { color: 'default', text: 'Not used by a customer yet' };
  const when = since(live.lastCalledAt);
  if (live.lastOutcome === 'ok' || live.lastOutcome === 'error_screen') {
    return { color: 'success', text: `Working · last used ${when}` };
  }
  return { color: 'error', text: `Last call failed ${when}: ${live.lastDetail || 'something went wrong'}` };
};

const Section = ({ title, subtitle, children }) => (
  <Box sx={{ py: 2 }}>
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>{title}</Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{subtitle}</Typography>
    )}
    {children}
  </Box>
);

/** Which diary a booking flow books into: its own binding, else the environment's, else ours. */
const diaryFor = (bindings, flowId) => {
  const own = bindings.find((binding) => binding.flowId === flowId);
  const fallback = bindings.find((binding) => !binding.flowId);
  const provider = (own || fallback)?.provider;
  return provider === 'CAL_COM' ? 'Cal.com' : 'your FlowGate diary';
};

/**
 * Everything one flow is connected to, in one place: how customers get it, the live data it asks
 * for while they fill it in, and where their answers go. Each section is a plain sentence plus a
 * status, so an owner can tell at a glance whether the flow is actually wired up and working.
 *
 * Keywords and destinations open their existing dialogs; live data is edited here because this is
 * the only place it lives. A demo account sees the flow's setup but is offered nothing the backend
 * would refuse it (DemoRestrictionFilter: destinations and live-data are not for demo accounts).
 */
const FlowConnectionsDrawer = ({ open, onClose, flow, onChanged }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { authenticatedFetch, isDemoMode } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const envId = selectedEnvironment?.id;
  const flowId = flow?.id;

  const [triggers, setTriggers] = useState(null);
  const [targets, setTargets] = useState(null);
  const [live, setLive] = useState(null);
  const [diary, setDiary] = useState(null);
  const [error, setError] = useState(null);
  const [triggersOpen, setTriggersOpen] = useState(false);
  const [destinationsOpen, setDestinationsOpen] = useState(false);

  const [editingUrl, setEditingUrl] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [liveError, setLiveError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [secret, setSecret] = useState(null);

  const load = useCallback(async () => {
    if (!envId || !flowId) return;
    setError(null);
    const [triggerList, summary, liveData] = await Promise.all([
      flowService.listFlowTriggers(envId, flowId, authenticatedFetch).catch(() => []),
      isDemoMode ? Promise.resolve(null)
        : deliveryService.destinationSummary(envId, authenticatedFetch).catch(() => null),
      flowService.getLiveData(envId, flowId, authenticatedFetch).catch((e) => { setError(e.message); return null; }),
    ]);
    setTriggers(triggerList);
    setTargets(summary ? (summary.flows?.[flowId] || summary.defaults || []) : null);
    setLive(liveData);
    setUrl(liveData?.forwardUrl || '');
    if (liveData?.bookingFlow) {
      const bindings = await bookingService.listProviders(envId, authenticatedFetch).catch(() => []);
      setDiary(diaryFor(Array.isArray(bindings) ? bindings : [], flowId));
    }
    // authenticatedFetch is recreated every AuthContext render; depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId, flowId, isDemoMode]);

  useEffect(() => {
    if (!open) return;
    setEditingUrl(false);
    setLiveError(null);
    setTestResult(null);
    setSecret(null);
    load();
  }, [open, load]);

  const run = async (action) => {
    setBusy(true);
    setLiveError(null);
    try {
      await action();
      onChanged?.();
    } catch (e) {
      setLiveError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveUrl = () => run(async () => {
    setLive(await flowService.forwardLiveData(envId, flowId, url.trim(), authenticatedFetch));
    setEditingUrl(false);
    setTestResult(null);
  });

  const stopForwarding = () => run(async () => {
    setLive(await flowService.stopForwardingLiveData(envId, flowId, authenticatedFetch));
    setUrl('');
    setSecret(null);
    setTestResult(null);
  });

  const test = () => run(async () => {
    setTestResult(await flowService.testLiveData(envId, flowId, authenticatedFetch));
  });

  const reveal = () => run(async () => {
    setSecret(await flowService.revealSigningSecret(envId, flowId, authenticatedFetch));
  });

  const rotate = () => run(async () => {
    setSecret(await flowService.rotateSigningSecret(envId, flowId, authenticatedFetch));
  });

  // The flow's live-data API, generated from the flow each time: what a developer builds against.
  const viewApiSpec = () => { onClose(); navigate(`/flows/${flowId}/api`); };

  const customerTriggers = (triggers || []).filter((t) => t.enabled !== false);
  const status = liveStatus(live);

  const liveDataBody = () => {
    if (!live) return error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={20} />;

    if (live.bookingFlow) {
      return (
        <Stack spacing={1}>
          <Typography variant="body1">
            Takes bookings into <strong>{diary || 'your FlowGate diary'}</strong>.
          </Typography>
          {!live.canServe ? (
            <Alert severity="warning">
              Connect your WhatsApp number to take live bookings. Publishing then sets everything else up.
            </Alert>
          ) : (
            <Chip size="small" color={status.color} label={status.text} sx={{ alignSelf: 'flex-start' }}
              data-testid="live-data-status" />
          )}
          {!isDemoMode && (
            <Button size="small" sx={{ alignSelf: 'flex-start' }}
              onClick={() => { onClose(); navigate('/settings?section=booking'); }}>
              Opening hours, services and diary
            </Button>
          )}
        </Stack>
      );
    }

    const forwarding = live.mode === 'FORWARD';
    if (!forwarding && !editingUrl) {
      return (
        <Stack spacing={1}>
          <Typography variant="body2">
            Nothing. This flow does not fetch anything while a customer fills it in, which is right
            for most forms.
          </Typography>
          {isDemoMode ? (
            <Typography variant="body2" color="text.secondary">
              Developers can connect their own server with an account.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setEditingUrl(true)} data-testid="live-data-connect-server">
                Connect your own server
              </Button>
              <Button size="small" onClick={viewApiSpec} data-testid="live-data-view-spec">
                View API spec
              </Button>
            </Stack>
          )}
        </Stack>
      );
    }

    return (
      <Stack spacing={1.5}>
        {editingUrl || !forwarding ? (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              While a customer is in the flow, FlowGate posts each step to your server as JSON, signed
              with <code>X-FlowGate-Signature</code>, and sends your reply back to WhatsApp. No keys or
              encryption on your side. <a href={DEVELOPERS_URL} target="_blank" rel="noreferrer">How it works</a>
            </Typography>
            <TextField
              size="small"
              label="Your server"
              placeholder="https://api.example.com/whatsapp"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              inputProps={{ 'data-testid': 'live-data-url' }}
              fullWidth
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" onClick={saveUrl} disabled={busy || !url.trim()}>
                Save
              </Button>
              <Button size="small" onClick={() => { setEditingUrl(false); setUrl(live.forwardUrl || ''); }}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Typography variant="body1">
              Calls <strong style={{ wordBreak: 'break-all' }}>{live.forwardUrl}</strong>
            </Typography>
            <Chip size="small" color={status.color} label={status.text} sx={{ alignSelf: 'flex-start' }}
              data-testid="live-data-status" />
            {live.pendingPublish && (
              <Alert severity="info">Publish the flow again for WhatsApp to start using this.</Alert>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" onClick={test} disabled={busy} data-testid="live-data-test">
                Test now
              </Button>
              <Button size="small" onClick={() => setEditingUrl(true)}>Change address</Button>
              <Button size="small" onClick={viewApiSpec} data-testid="live-data-view-spec">View API spec</Button>
              <Button size="small" color="error" onClick={stopForwarding} disabled={busy}>Stop</Button>
            </Stack>
            {testResult && (
              <Alert severity={testResult.ok ? 'success' : 'error'} data-testid="live-data-test-result">
                {testResult.detail}
              </Alert>
            )}
            <Divider flexItem />
            <Typography variant="body2" color="text.secondary">Signing secret</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                data-testid="live-data-secret">
                {secret || '••••••••••••••••'}
              </Typography>
              {secret ? (
                <Tooltip title="Copy">
                  <IconButton size="small" aria-label="Copy signing secret"
                    onClick={() => navigator.clipboard?.writeText(secret)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button size="small" onClick={reveal} disabled={busy}>Show</Button>
              )}
              <Button size="small" onClick={rotate} disabled={busy}>New secret</Button>
            </Stack>
          </Stack>
        )}
        {liveError && <Alert severity="error" data-testid="live-data-error">{liveError}</Alert>}
      </Stack>
    );
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: fullScreen ? '100%' : 440 } }}>
        <Box sx={{ px: 3, pt: 2, pb: 3 }} data-testid="flow-connections">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Connections</Typography>
              <Typography variant="body2" color="text.secondary">{flow?.name}</Typography>
            </Box>
            <IconButton aria-label="Close connections" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>

          <Section title="How customers get it">
            {triggers === null ? <CircularProgress size={20} /> : (
              <Stack spacing={1}>
                {customerTriggers.length === 0 ? (
                  <Typography variant="body2">
                    No keywords yet. Customers get it when you send it or share its link.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 0.5 }}>Sent when a customer says</Typography>
                    {customerTriggers.map((t) => (
                      <Chip key={t.id} size="small" label={t.triggerPhrase || t.intentDescription || 'intent'} />
                    ))}
                  </Box>
                )}
                <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setTriggersOpen(true)}
                  data-testid="connections-edit-keywords">
                  {customerTriggers.length === 0 ? 'Add a keyword' : 'Edit keywords'}
                </Button>
              </Stack>
            )}
          </Section>
          <Divider />

          <Section title="Live data" subtitle="What the flow looks up while a customer fills it in.">
            {liveDataBody()}
          </Section>
          <Divider />

          <Section title="Where answers go">
            {isDemoMode ? (
              <Typography variant="body2" color="text.secondary">
                Answers are listed under Submissions. Sending them to email, a sheet or your own system
                comes with an account.
              </Typography>
            ) : targets === null ? <CircularProgress size={20} /> : (
              <Stack spacing={1}>
                {targets.length === 0 ? (
                  <Typography variant="body2">Listed under Submissions only.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {targets.map((target) => (
                      <Chip key={target.type} size="small" variant="outlined"
                        icon={connectorIconFor(target.type)} label={target.displayName} />
                    ))}
                  </Box>
                )}
                <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setDestinationsOpen(true)}
                  data-testid="connections-edit-destinations">
                  {targets.length === 0 ? 'Send answers somewhere' : 'Change'}
                </Button>
              </Stack>
            )}
          </Section>
        </Box>
      </Drawer>

      <FlowTriggerDialog open={triggersOpen} flow={flow}
        onClose={() => { setTriggersOpen(false); load(); onChanged?.(); }} />
      {!isDemoMode && (
        <DestinationDialog open={destinationsOpen} flow={destinationsOpen ? flow : null}
          onClose={() => { setDestinationsOpen(false); load(); onChanged?.(); }} />
      )}
    </>
  );
};

export default FlowConnectionsDrawer;
